import React from 'react';
import { X } from 'lucide-react';
import { PipelineRecord } from '../data/mockData';
interface PipelineDetailModalProps {
  title: string;
  subtitle: string;
  totalValue: number;
  records: PipelineRecord[];
  onClose: () => void;
  accentColor: 'cyan' | 'emerald' | 'blue' | 'purple';
}
export function PipelineDetailModal({
  title,
  subtitle,
  totalValue,
  records,
  onClose,
  accentColor
}: PipelineDetailModalProps) {
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(val);
  const accentStyles = {
    cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/30'
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Funded':
      case 'Clear to Close':
        return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'Cond. Approval':
      case 'Locked':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'Underwriting':
      case 'Processing':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default:
        return 'bg-slate-700 text-slate-300 border-slate-600';
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-320">
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-321" />

      <div className="relative bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-322">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-800 bg-slate-900/50" data-id="element-323">
          <div data-id="element-324">
            <div className="flex items-center gap-3 mb-1" data-id="element-325">
              <h2 className="text-xl font-bold text-white" data-id="element-326">{title}</h2>
              <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${accentStyles[accentColor]}`} data-id="element-327">
                {records.length} Loans
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-328">
              <p className="text-sm text-slate-400" data-id="element-329">{subtitle}</p>
              <span className="text-slate-600" data-id="element-330">•</span>
              <p className="text-sm font-medium text-slate-300" data-id="element-331">
                Total Volume:{' '}
                <span className="text-white font-bold" data-id="element-332">
                  {formatCurrency(totalValue)}
                </span>
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-333">
            <X className="w-5 h-5" data-id="element-334" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar" data-id="element-335">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden" data-id="element-336">
            <div className="overflow-x-auto" data-id="element-337">
              <table className="w-full text-left border-collapse whitespace-nowrap" data-id="element-338">
                <thead data-id="element-339">
                  <tr className="bg-slate-800 border-b border-slate-700 text-xs uppercase tracking-wider text-slate-400" data-id="element-340">
                    <th className="px-5 py-4 font-medium" data-id="element-341">Borrower</th>
                    <th className="px-5 py-4 font-medium" data-id="element-342">Status</th>
                    <th className="px-5 py-4 font-medium text-right" data-id="element-343">
                      Loan Amount
                    </th>
                    <th className="px-5 py-4 font-medium text-right" data-id="element-344">Rate</th>
                    <th className="px-5 py-4 font-medium" data-id="element-345">Est. Close</th>
                    <th className="px-5 py-4 font-medium" data-id="element-346">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50" data-id="element-347">
                  {records.map(record => <tr key={record.id} className="hover:bg-slate-700/30 transition-colors group" data-id="element-348">
                      <td className="px-5 py-4 text-sm font-medium" data-id="element-349">
                        <button onClick={() => {}} className="text-cyan-400 hover:text-cyan-300 hover:underline transition-colors cursor-pointer" data-id="element-350">
                          {record.name}
                        </button>
                      </td>
                      <td className="px-5 py-4 text-sm" data-id="element-351">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${getStatusColor(record.status)}`} data-id="element-352">
                          {record.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm font-semibold text-slate-200 text-right" data-id="element-353">
                        {formatCurrency(record.amount)}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-300 text-right" data-id="element-354">
                        {record.rate}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-300" data-id="element-355">
                        {record.closeDate}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-400 max-w-[200px] truncate group-hover:text-slate-300 transition-colors" title={record.notes} data-id="element-356">
                        {record.notes}
                      </td>
                    </tr>)}
                  {records.length === 0 && <tr data-id="element-357">
                      <td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-500" data-id="element-358">
                        No loans found in this category.
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <style data-id="element-359">{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #475569; }
      `}</style>
    </div>;
}